import { useState } from 'react';

/**
 * TagInput — chip/tag input with keyboard and paste support.
 *
 * Props:
 *   tags         — string[]
 *   onChange     — called with new string[] when tags change
 *   placeholder  — hint shown when the input is empty
 *   tagColor     — chip text/border color (default: #2980b9)
 *   tagBg        — chip background color (default: #eef4ff)
 *   splitOnComma — if true, typing comma commits the current value (default: true)
 *
 * Keyboard:
 *   Enter / comma — commit current input as a new tag
 *   Backspace (on empty input) — remove last tag
 *
 * Paste:
 *   Multi-line paste is split on newlines and added as separate tags.
 *   Single-line paste falls through to normal input behaviour.
 */
export default function TagInput({ tags, onChange, placeholder, tagColor = '#2980b9', tagBg = '#eef4ff', splitOnComma = true }) {
  const [input, setInput] = useState('');

  const addValues = (vals) => {
    const cleaned = vals.map(v => v.trim()).filter(v => v && !tags.includes(v));
    if (cleaned.length) onChange([...tags, ...cleaned]);
  };

  const commit = () => { addValues([input]); setInput(''); };

  const handleKey = (e) => {
    if (e.key === 'Enter') { e.preventDefault(); commit(); }
    if (splitOnComma && e.key === ',') { e.preventDefault(); commit(); }
    if (e.key === 'Backspace' && !input && tags.length) onChange(tags.slice(0, -1));
  };

  const handlePaste = (e) => {
    const text = e.clipboardData.getData('text');
    if (text.includes('\n')) {
      e.preventDefault();
      addValues(text.split('\n'));
      setInput('');
    }
    // single-line paste falls through to normal input
  };

  return (
    <div style={{
      display: 'flex', flexWrap: 'wrap', gap: '4px',
      border: '1px solid #ccc', borderRadius: '4px', padding: '4px 8px',
      background: '#fff', minHeight: '34px', alignItems: 'center',
    }}>
      {tags.map(t => (
        <span key={t} style={{
          display: 'inline-flex', alignItems: 'center', gap: '4px',
          padding: '2px 8px', borderRadius: '12px',
          background: tagBg, color: tagColor, border: `1px solid ${tagColor}33`, fontSize: '12px',
        }}>
          {t}
          <span
            onClick={() => onChange(tags.filter(x => x !== t))}
            style={{ cursor: 'pointer', fontWeight: '700', color: tagColor, lineHeight: 1 }}
          >×</span>
        </span>
      ))}
      <input
        value={input}
        onChange={e => setInput(e.target.value)}
        onKeyDown={handleKey}
        onBlur={commit}
        onPaste={handlePaste}
        placeholder={tags.length ? '' : placeholder}
        style={{
          border: 'none', outline: 'none', fontSize: '12px',
          flex: 1, minWidth: '120px', background: 'transparent',
        }}
      />
    </div>
  );
}
